<template>
	<v-chart
		ref="chart"
		class="chart"
		:option="option"
		:init-options="{ renderer: 'svg' }"
		autoresize
	/>
</template>




<script>
// https://github.com/ecomfe/vue-echarts?tab=readme-ov-file
// https://echarts.apache.org/handbook/en/concepts/dataset/#
import VChart from 'vue-echarts'
import { Post } from '@/models/post.js'
import { usePostsStore } from '@/stores/PostsStore'
import { readPostReportTable } from '@/api/api.js'
import { chartTimeStr, chartDateStr, periodDays } from '@/helpers.js'

import { use } from 'echarts/core'
import {
	LineChart
} from 'echarts/charts'
import {
	TitleComponent,
	LegendComponent,
	TooltipComponent,
	GridComponent,
	DataZoomComponent,
	VisualMapComponent,
	MarkLineComponent
} from 'echarts/components'
import {
	CanvasRenderer,
	SVGRenderer,
} from 'echarts/renderers'
use([
	TitleComponent,
	LegendComponent,
	TooltipComponent,
	GridComponent,
	DataZoomComponent,
	VisualMapComponent,
	MarkLineComponent,
	LineChart,
	CanvasRenderer,
	SVGRenderer,
])

let xAxisLabelTmp = null

export default {
	// Colors
	TITLE_COLOR: '#2c3e50',
	POTENTIAL_COLOR: '#5794f2',
	RED: '#f2495c',
	ORANGE: '#ff9830',
	BLUE: '#5794f2',
	//
	POTENTIAL_AXIS_NAME: 'Захисний потенціал',

	props: {
		postId: {
			type: String,
			require: true,
		},
		timeFrom: {
			type: Date,
			require: false,
			default: () => {
				const now = new Date()
				now.setMonth(now.getMonth() - 1)
				return now
			},
		},
		timeTo: {
			type: Date,
			require: false,
			default: new Date(),
		},
		isDataZoom: {
			type: Boolean,
			require: false,
			default: true,
		},
	},

	emits: {
		'loading': value => typeof value === "boolean",
	},

	components: {
		VChart,
	},

	setup() {
		const postsStore = usePostsStore()

		return {
			Post,
			postsStore,
		}
	},

	data() {
		return {
			// Graph configuration
			option: {
				color: [ this.$options.POTENTIAL_COLOR, ],
				title: [
					{
						left: 'center',
						text: this.$options.POTENTIAL_AXIS_NAME,
						textStyle: {
							color: this.$options.TITLE_COLOR,
							fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
							fontStyle: 'normal',
							fontSize: 14.28,
							fontWeight: 700,
						},
						textAlign: 'auto',
						padding: [8, 100],
					}
				],
				// legend: {
				// 	left: 'center',
				// 	bottom: 5,
				// 	selectedMode: 'muultiple', // can select multiple lines for show
				// },
				dataZoom: [
					// Slider on bottom of the chart
					{
						type: 'slider',
						show: true,
						realtime: true,
						xAxisIndex: [0, 1, 2],
						bottom: 50,
						dataBackground: {
							areaStyle: {
								opacity: 0.1,
							}
						},
						labelFormatter: function (value, valueStr) {
							const date = new Date(valueStr);
							return `${chartDateStr(date)}\n${chartTimeStr(date)}`
						}
					},
					// For touchscreen
					{
						type: 'inside',
						disabled: false,
						realtime: true,
						xAxisIndex: [0, 1, 2]
					},
				],
				grid: [ // need for padding control (make smaller padding)
					{ left: 60, right: 60, top: 60, bottom: 60 },
				],
				tooltip: {
					trigger: 'axis',
					// Tooltip builder function
					formatter: (params/*, ticket, callback*/) => {
						const parseData = (data) => data === 'NaN' ? '-' : data

						// Get first valid date
						let date =new Date(new Date(params[0].axisValue).getTime())

						// Addtooltip header
						let tooltip = `<div class="container">`
						tooltip += `<div class="row"><div class="col text-center">`
						tooltip += ` ${chartDateStr(date)} ${chartTimeStr(date)}`
						tooltip += `</div></div>`

						// Add tooltip body
						tooltip += `<div class="row justify-content-between">`
						tooltip += `<div class="col ms-0 text-start"><span style="color: ${params[0].color};">&#9210;</span> ${params[0].seriesName}</div>`
						tooltip += `<div class="col-auto ms-auto me-0 text-end fw-bolder">${parseData(params[0].data[1])}</div>`
						tooltip += `</div>`

						tooltip += `</div>`
						return tooltip
					}
				},
				xAxis: [
					{
						gridIndex: 0, // attach to grid
						type: 'time',
						boundaryGap: false,
						data: [],
						axisLabel: {
							hideOverlap: true,
							formatter: (value, /*idx*/) => {
								var local = new Date(new Date(value).getTime())

								// If first axis label
								if (xAxisLabelTmp == null) {
									xAxisLabelTmp = local
									return `${chartDateStr(local)}`
								}
								
								// If period < 7 days
								if (periodDays(this.timeFrom, this.timeTo) < 7) {
									// If first label in day
									if (local.getDate() != xAxisLabelTmp.getDate()) {
										xAxisLabelTmp = local
										return `${chartDateStr(local)}`
									} else {
										return `${chartTimeStr(local)}`
									}
								// If period >= 7 days and < 1 year
								} else if (periodDays(this.timeFrom, this.timeTo) < 363) {
									return `${chartDateStr(local)}`
								} else {
									// If first label in year
									if (local.getFullYear() != xAxisLabelTmp.getFullYear()) {
										xAxisLabelTmp = local
										return `${local.getFullYear()}`
									} else {
										return chartDateStr(local)//`${local.getMonth()+1}`
									}
								}
							}
						},
					}
				],
				yAxis: [
					// Potential
					{
						gridIndex: 0,
						type: 'value',
						name: '',
						position: 'left',
						alignTicks: false,
						min: -5.0,
						max: 2.0,
						scale: true,
						//splitNumber: 12,
						//interval: 0.5,
						minInterval: 0.5,
						maxInterval: 1,
						// Vertical line of axis
						axisLine: {
							show: true,
							lineStyle: {
								color: this.$options.TITLE_COLOR,
							}
						},
						// Grid lines of axis
						splitLine: {
							show: true,
						},
						axisLabel: {
							formatter: '{value}'
						},
						axisPointer: {
							snap: true
						}
					},
				],
				// Add style to lines
				visualMap: [
					// Potential
					{
						type: 'piecewise',
						show: false,
						top: 50,
						right: 1200,
						pieces: [
							{
								lt: 0,
								gte: -0.85,
								color: this.$options.RED,
							},
							{
								lt: -0.85,
								gte: -2.5,
								color: this.$options.BLUE,
							},
							{
								lt: -2.5,
								gte: -3.5,
								color: this.$options.ORANGE,
							},
							{
								lt: -3.5,
								gte: -5,
								color: this.$options.RED,
							},
						],
						outOfRange: {
							color: this.$options.RED,
						},
					}
				],
				series: [
					// Potential
					{
						name: this.$options.POTENTIAL_AXIS_NAME,
						type: 'line',
						data: [],
						smooth: false,
						lineStyle: {
							width: 1,
						},
						showSymbol: false,
						areaStyle: { // enable area gradient (by visual map)
							opacity: 0.15,
						},
					},
				]
			},

			isLoading: true,
		}
	},

	watch: {
		// Update chart on timeFrom update
		timeFrom(/*newTime, oldTime*/) {
			if (!this.isLoading) {
				this.readValues()
			}
		},
		// Update chart on timeTo update
		timeTo(/*newCriterias*/) {
			if (!this.isLoading) {
				this.readValues()
			}
		},
	},

	async created() {
		await this.readValues()

		this.setDataZoomVisibility(true)

		// Adaptive yAxis label interval depend on chart height
		//console.log('height = ', this.$refs.chart.$el.clientHeight)
		if (this.$refs.chart.$el.clientHeight <= 400) {
			this.option.yAxis[0].interval = 1
		} else {
			this.option.yAxis[0].interval = 0.5
		}
	},

	mounted() {},

	methods: {
		async readValues() {
			this.setLoading(true)

			const postMetrics = []
			await readPostReportTable(
				this.post.passport.get(Post.passportKeys.metricsId),
				this.timeFrom,
				this.timeTo,
				postMetrics
			).then(result => {
				if (!result) {
					return
				}
				// console.log("Fetch post metrics: ", result, postMetrics)
				postMetrics.sort((a, b) => a.time.getTime() > b.time.getTime())
				this.option.xAxis[0].data = postMetrics.map((el) => el.time)
				this.option.series[0].data = postMetrics.map((el) =>
					[ el.time, Number(el[Post.metricsKeys.Eavg]).toFixed(2), ]
				)
			}).catch(err => {
				console.error("Error on read post potentials: ", err)
			})

			this.setLoading(false)

			return postMetrics
		},

		setLoading(loadingState) {
			this.isLoading = Boolean(loadingState)

			this.$emit('loading', this.isLoading)

			if (this.isLoading) {
				this.chartInstance?.showLoading(
					'default',
					{
						text: '',
						spinnerRadius: 20,
						color: '#2c3e50', // spiner color
						maskColor: 'rgba(255, 255, 255, 0.8)', // background
					}
				)
			} else {
				this.chartInstance?.hideLoading()
			}
		},

		setDataZoomVisibility(isVisible) {
			if (isVisible) {
				this.option.grid[0].bottom = 120
				this.option.dataZoom[0].show = true
				this.option.dataZoom[1].disabled = false
			} else {
				this.option.dataZoom[0].show = false
				this.option.grid[0].bottom = 60
				this.option.dataZoom[1].disabled = true
			}
		},
	},

	computed: {
		post() {
			return this.postsStore.post(this.postId)
		},

		chartInstance() {
			return this.$refs.chart?.chart
		},
	},
}
</script>




<style scoped>
.chart {
	height: 100%;
}
</style>